import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import { useRestaurant } from "./RestaurantContext";

interface Invite {
  id: number;
  email: string;
  status: string;
  createdAt: string;
  invitedBy: { name: string };
  restaurant: { name: string };
  inviteLink?: string;
}

const STATUS_KEYS: Record<string, string> = {
  pending: "common.pending",
  accepted: "common.accepted",
  revoked: "common.revoked",
};

const Invites: React.FC = () => {
  const { t } = useTranslation();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const { currentRestaurantId: restaurantId } = useRestaurant();

  useEffect(() => {
    if (isAdmin && restaurantId) fetchInvites();
    // eslint-disable-next-line
  }, [restaurantId]);

  const fetchInvites = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurantId}/invites`);
      if (res && res.ok) {
        let data = await res.json();
        data = await Promise.all(data.map(async (invite: Invite) => {
          if (invite.status === 'pending') {
            // Try to get the inviteLink from the backend (simulate by calling /api/users/invite again)
            // In production, you should store the inviteLink in the DB and return it with the invite
            // For now, just omit if not available
            // Optionally, you could add a backend endpoint to fetch the inviteLink by invite id
          }
          return invite;
        }));
        setInvites(data);
      } else {
        setError(t("invites.failedToFetch"));
      }
    } catch (err) {
      setError(t("invites.failedToFetch"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (invite: Invite) => {
    const link = invite.inviteLink
      ? invite.inviteLink
      : `${window.location.origin}/accept-invite?token=${invite.id}`;
    navigator.clipboard.writeText(link);
    alert(t("invites.linkCopied"));
  };

  const handleRevoke = async (invite: Invite) => {
    if (!window.confirm(t("invites.revokeConfirm", { email: invite.email }))) return;
    try {
      const res = await apiFetch(`/api/invites/${invite.id}/revoke`, {
        method: 'PATCH',
        
      });
      if (!res.ok) {
        const data = await res.json();
        alert(data.error || t('invites.failedRevoke'));
      } else {
        await fetchInvites();
      }
    } catch {
      alert(t('invites.failedRevoke'));
    }
  };

  if (!isAdmin) {
    return <div className="p-6">{t('invites.onlyAdmins')}</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">{t('invites.management')}</h1>
      {loading ? (
        <div>{t('invites.loading')}</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : invites.filter(invite => invite.status === 'pending').length === 0 ? (
        <div>{t('invites.noPending')}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded shadow">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">{t('common.email')}</th>
                <th className="px-4 py-2 text-left">{t('common.status')}</th>
                <th className="px-4 py-2 text-left">{t('invites.invitedAt')}</th>
                <th className="px-4 py-2 text-left">{t('invites.invitedBy')}</th>
                <th className="px-4 py-2 text-left">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {invites.filter(invite => invite.status === 'pending').map((invite) => (
                <tr key={invite.id} className="border-t">
                  <td className="px-4 py-2">{invite.email}</td>
                  <td className="px-4 py-2 capitalize">{t(STATUS_KEYS[invite.status] || invite.status)}</td>
                  <td className="px-4 py-2">{new Date(invite.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-2">{invite.invitedBy?.name || "-"}</td>
                  <td className="px-4 py-2 flex gap-2">
                    <button
                      className="px-2 py-1 bg-teal-500 text-white rounded hover:bg-teal-600"
                      onClick={() => handleCopyLink(invite)}
                    >
                      {t('invites.copyLink')}
                    </button>
                    <button
                      className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      onClick={() => handleRevoke(invite)}
                    >
                      {t('invites.revoke')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Invites; 